import { useEffect, useMemo, useRef, type ReactNode } from "react";
import type { i18n as I18nInstance } from "i18next";
import { LiveProvider, QueryClientProvider, SessionProvider, api, createQueryClient, isApiError, useLiveEvent, useQueryClient, useSession } from "@pmo/api-client";
import { I18nProvider, useLocale } from "@pmo/i18n";
import { Toaster, UiProvider, toast } from "@pmo/ui";

/** Localised toast for a failed request (used by the query client for mutations and opted-in queries). */
export function toastError(i18n: I18nInstance, error: unknown): void {
  const t = (key: string) => i18n.t(key, { ns: "common" });
  if (isApiError(error)) {
    if (error.isUnavailable) return void toast.error(t("errors.apiUnavailableTitle"), { description: t("errors.apiUnavailableBody") });
    if (error.isForbidden) return void toast.error(t("errors.forbiddenTitle"), { description: error.detail ?? t("errors.forbiddenBody") });
    if (error.status === 409) return void toast.error(t("errors.conflict"), { description: error.detail ?? undefined });
    if (error.isValidation && Object.keys(error.errors).length) return void toast.error(t("errors.validation"), { description: error.detail });
    return void toast.error(error.problem?.title ?? t("errors.genericTitle"), {
      description: [error.detail, error.traceId ? i18n.t("errors.correlation", { ns: "common", id: error.traceId }) : null].filter(Boolean).join(" "),
    });
  }
  toast.error(t("errors.genericTitle"), { description: t("errors.genericBody") });
}

function Direction({ children }: { children: ReactNode }) {
  const { dir } = useLocale();
  return (
    <UiProvider dir={dir}>
      {children}
      <Toaster dir={dir} />
    </UiProvider>
  );
}

function Live({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useSession();
  return (
    <LiveProvider enabled={isAuthenticated}>
      <TranslationRefresh />
      {children}
    </LiveProvider>
  );
}

/** Refetches what is on screen once machine translations land; bursts (e.g. a backfill) are folded into one refresh. */
function TranslationRefresh() {
  const qc = useQueryClient();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  useLiveEvent("translation.completed", () => {
    if (timer.current) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      void qc.invalidateQueries({ refetchType: "active" });
    }, 1200);
  });
  return null;
}

/** Refetches what is on screen after a language switch, so text the server words in the reader's language follows it. */
function LanguageRefresh({ i18n }: { i18n: I18nInstance }) {
  const qc = useQueryClient();
  useEffect(() => {
    const refresh = () => void qc.invalidateQueries({ refetchType: "active" });
    i18n.on("languageChanged", refresh);
    return () => i18n.off("languageChanged", refresh);
  }, [i18n, qc]);
  return null;
}

/** Saves a language switch to the user's profile, so notifications and generated documents follow the same choice. */
function LocaleSync({ i18n }: { i18n: I18nInstance }) {
  const { isAuthenticated } = useSession();
  useEffect(() => {
    if (!isAuthenticated) return;
    const save = (lng: string) => void api.put("/api/v1/me/locale", { locale: lng.startsWith("ar") ? "ar" : "en" }).catch(() => undefined);
    i18n.on("languageChanged", save);
    return () => i18n.off("languageChanged", save);
  }, [isAuthenticated, i18n]);
  return null;
}

/**
 * Everything an app needs around its router: i18n, the Radix reading direction, toasts, TanStack Query with the platform
 * defaults, the session (with tenant accent) and the live channel. The public portal passes `session={false}`.
 */
export function AppProviders({ i18n, children, live = true, session = true }: { i18n: I18nInstance; children: ReactNode; live?: boolean; session?: boolean }) {
  const queryClient = useMemo(
    () =>
      createQueryClient({
        onError: (e) => toastError(i18n, e),
        onSuccessMessage: (m) => toast.success(m),
      }),
    [i18n],
  );
  return (
    <I18nProvider i18n={i18n}>
      <Direction>
        <QueryClientProvider client={queryClient}>
          <LanguageRefresh i18n={i18n} />
          {session ? (
            <SessionProvider>
              <LocaleSync i18n={i18n} />
              {live ? <Live>{children}</Live> : children}
            </SessionProvider>
          ) : (
            children
          )}
        </QueryClientProvider>
      </Direction>
    </I18nProvider>
  );
}
